import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../contexts/AppContext';
import { accountsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import PeriodToolbar from '../components/PeriodToolbar';
import { usePeriod } from '../hooks/usePeriod';
import { PERIOD_MODES } from '../lib/period';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Search, Activity, Plus, Pencil, Trash2, 
  ChevronLeft, ChevronRight, Clock, User, FileText,
  ArrowRight, RefreshCw, Eye
} from 'lucide-react';
import { toast } from 'sonner';

const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatTime = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const ActionBadge = ({ action }) => {
  const variants = {
    CREATE: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    UPDATE: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    DELETE: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
    ALLOCATE: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
    LOCK: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    UNLOCK: 'bg-teal-500/10 text-teal-400 border-teal-500/20',
    SYNC: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
  };
  const icons = {
    CREATE: Plus,
    UPDATE: Pencil,
    DELETE: Trash2,
  };
  const Icon = icons[action] || Activity;
  
  return (
    <Badge variant="outline" className={`gap-1 ${variants[action] || 'bg-gray-500/10 text-gray-400'}`}>
      <Icon className="w-3 h-3" />
      {action}
    </Badge>
  );
};

const EntityTypeBadge = ({ type }) => {
  const variants = {
    BILL: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
    PAYMENT: 'bg-green-500/10 text-green-400 border-green-500/20',
    CREDIT_NOTE: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    DEBIT_NOTE: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
    VENDOR: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    PAYMENT_ALLOCATION: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
    PERIOD_LOCK: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  };
  
  return (
    <Badge variant="outline" className={variants[type] || 'bg-gray-500/10 text-gray-400'}>
      {type?.replace(/_/g, ' ')}
    </Badge>
  );
};

// JSON Diff Component
const JsonDiff = ({ before, after }) => {
  const renderValue = (value) => {
    if (value === null || value === undefined) return <span className="text-muted-foreground">null</span>;
    if (typeof value === 'boolean') return <span className="text-blue-400">{value.toString()}</span>;
    if (typeof value === 'number') return <span className="text-emerald-400">{value.toLocaleString()}</span>;
    if (typeof value === 'string') return <span className="text-amber-400">"{value}"</span>;
    if (Array.isArray(value)) return <span className="text-muted-foreground">[{value.length} items]</span>;
    if (typeof value === 'object') return <span className="text-muted-foreground">{'{...}'}</span>;
    return String(value);
  };

  const allKeys = new Set([...Object.keys(before || {}), ...Object.keys(after || {})]);
  const sortedKeys = Array.from(allKeys).sort();

  if (sortedKeys.length === 0) {
    return <p className="text-muted-foreground text-sm">No changes recorded</p>;
  }

  return (
    <div className="space-y-1 font-mono text-sm">
      {sortedKeys.map(key => {
        const oldVal = before?.[key];
        const newVal = after?.[key];
        const isAdded = !(key in (before || {}));
        const isRemoved = !(key in (after || {}));
        const isChanged = !isAdded && !isRemoved && JSON.stringify(oldVal) !== JSON.stringify(newVal);

        if (isAdded) {
          return (
            <div key={key} className="flex items-start gap-2 text-emerald-400 bg-emerald-500/5 px-2 py-1 rounded">
              <Plus className="w-3 h-3 mt-1 flex-shrink-0" />
              <span className="font-medium">{key}:</span>
              {renderValue(newVal)}
            </div>
          );
        }
        if (isRemoved) {
          return (
            <div key={key} className="flex items-start gap-2 text-rose-400 bg-rose-500/5 px-2 py-1 rounded">
              <Trash2 className="w-3 h-3 mt-1 flex-shrink-0" />
              <span className="font-medium">{key}:</span>
              {renderValue(oldVal)}
            </div>
          );
        }
        if (isChanged) {
          return (
            <div key={key} className="flex items-start gap-2 text-blue-400 bg-blue-500/5 px-2 py-1 rounded">
              <Pencil className="w-3 h-3 mt-1 flex-shrink-0" />
              <span className="font-medium">{key}:</span>
              <span className="text-rose-400 line-through opacity-60">{renderValue(oldVal)}</span>
              <ArrowRight className="w-3 h-3" />
              <span className="text-emerald-400">{renderValue(newVal)}</span>
            </div>
          );
        }
        return null; // Unchanged keys
      })}
    </div>
  );
};

// Raw JSON Display
const RawJson = ({ data, title }) => {
  if (!data || Object.keys(data).length === 0) {
    return <p className="text-muted-foreground text-sm">No data available</p>;
  }
  
  return (
    <pre className="bg-background/50 border border-border/50 rounded-lg p-4 overflow-auto text-xs font-mono text-muted-foreground">
      {JSON.stringify(data, null, 2)}
    </pre>
  );
};

const AuditLogPage = () => {
  const { selectedOrg } = useApp();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(null);
  const [search, setSearch] = useState('');
  const [entityType, setEntityType] = useState('ALL');
  const [action, setAction] = useState('ALL');
  const periodState = usePeriod(PERIOD_MODES.RANGE_ALL_TIME);
  const [resolvedPeriod, setResolvedPeriod] = useState(null);
  const periodKey = JSON.stringify(periodState.apiParams);
  const [page, setPage] = useState(0);
  useEffect(() => { setPage(0); }, [periodKey]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const limit = 30;

  useEffect(() => {
    if (selectedOrg) {
      loadFilters();
      loadLogs();
    }
  }, [selectedOrg]);

  useEffect(() => {
    if (selectedOrg) {
      loadLogs();
    }
  }, [page, entityType, action, periodState.apiParams]);

  const loadFilters = async () => {
    try {
      const response = await accountsAPI.getAuditLogFilters();
      setFilters(response.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
    }
  };

  const loadLogs = async () => {
    if (!selectedOrg) return;
    setLoading(true);
    try {
      const params = {
        skip: page * limit,
        limit,
      };
      if (entityType && entityType !== 'ALL') params.entity_type = entityType;
      if (action && action !== 'ALL') params.action = action;
      if (search) params.q = search;
      Object.assign(params, periodState.apiParams);

      const response = await accountsAPI.getAuditLogs(params);
      setResolvedPeriod(response.data);
      setLogs(response.data.logs);
      setTotal(response.data.total);
      setSummary(response.data.summary);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
      toast.error('Failed to load audit logs');
    }
    setLoading(false);
  };

  const handleSearch = useCallback(() => {
    setPage(0);
    loadLogs();
  }, [search]);

  const openLogDetail = async (log) => {
    try {
      const response = await accountsAPI.getAuditLog(log.id);
      setSelectedLog(response.data);
      setDrawerOpen(true);
    } catch (err) {
      // Fallback to the log from the list
      setSelectedLog(log);
      setDrawerOpen(true);
    }
  };

  const getSummaryText = (log) => {
    const details = log.details || {};
    if (log.action === 'CREATE') {
      if (details.amount) return `Created with amount ₹${details.amount.toLocaleString()}`;
      if (details.bill_number) return `Bill #${details.bill_number} created`;
      if (details.note_number) return `Note ${details.note_number} created`;
      return 'New record created';
    }
    if (log.action === 'UPDATE') {
      if (details.updated_fields) return `Updated: ${details.updated_fields.join(', ')}`;
      return 'Record updated';
    }
    if (log.action === 'DELETE') {
      if (details.bill_number) return `Bill #${details.bill_number} deleted`;
      return 'Record deleted';
    }
    if (log.action === 'ALLOCATE') {
      return `Allocated ₹${details.amount?.toLocaleString() || '?'}`;
    }
    return log.action;
  };

  const totalPages = Math.ceil(total / limit);

  if (!selectedOrg) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Please select an organization</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-manrope font-bold tracking-tight">Audit Log</h1>
          <p className="text-muted-foreground mt-1">
            Complete audit trail of all system mutations
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => { setPage(0); loadLogs(); }}
          className="gap-2"
          data-testid="refresh-btn"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-card/50 backdrop-blur-sm border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Events</p>
                  <p className="text-2xl font-bold font-mono">{summary.total_events}</p>
                </div>
                <Activity className="w-8 h-8 text-muted-foreground/50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-emerald-500/5 border-emerald-500/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-emerald-400">Creates</p>
                  <p className="text-2xl font-bold font-mono text-emerald-400">{summary.creates}</p>
                </div>
                <Plus className="w-8 h-8 text-emerald-400/50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-blue-500/5 border-blue-500/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-400">Updates</p>
                  <p className="text-2xl font-bold font-mono text-blue-400">{summary.updates}</p>
                </div>
                <Pencil className="w-8 h-8 text-blue-400/50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-rose-500/5 border-rose-500/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-rose-400">Deletes</p>
                  <p className="text-2xl font-bold font-mono text-rose-400">{summary.deletes}</p>
                </div>
                <Trash2 className="w-8 h-8 text-rose-400/50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card/50 backdrop-blur-sm border-border/50">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground mb-2">Top Entities</p>
              <div className="flex flex-wrap gap-1">
                {summary.top_entity_types?.slice(0, 3).map(e => (
                  <Badge key={e.type} variant="outline" className="text-xs">
                    {e.type?.replace(/_/g, ' ')} ({e.count})
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="bg-card/50 backdrop-blur-sm border-border/50">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by vendor, invoice, reference..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10 bg-background/50"
                data-testid="audit-search"
              />
            </div>
            <PeriodToolbar state={periodState} mode={PERIOD_MODES.RANGE_ALL_TIME} resolved={resolvedPeriod} />
            <Select value={entityType} onValueChange={(v) => { setEntityType(v); setPage(0); }}>
              <SelectTrigger className="w-[180px] bg-background/50" data-testid="entity-type-filter">
                <SelectValue placeholder="All entity types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All entity types</SelectItem>
                {filters?.entity_types?.map(type => (
                  <SelectItem key={type} value={type}>{type?.replace(/_/g, ' ')}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={action} onValueChange={(v) => { setAction(v); setPage(0); }}>
              <SelectTrigger className="w-[140px] bg-background/50" data-testid="action-filter">
                <SelectValue placeholder="All actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All actions</SelectItem>
                {filters?.actions?.map(a => (
                  <SelectItem key={a} value={a}>{a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card className="bg-card/50 backdrop-blur-sm border-border/50">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-4">
              {[...Array(8)].map((_, i) => (
                <Skeleton key={`skeleton-${i}`} className="h-14 w-full" />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-border/50 hover:bg-transparent">
                  <TableHead className="font-manrope w-[160px]">Time</TableHead>
                  <TableHead className="font-manrope">Entity</TableHead>
                  <TableHead className="font-manrope w-[120px]">Action</TableHead>
                  <TableHead className="font-manrope">User</TableHead>
                  <TableHead className="font-manrope">Summary</TableHead>
                  <TableHead className="font-manrope w-[60px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => (
                  <TableRow 
                    key={log.id} 
                    className="border-border/50 hover:bg-muted/50 cursor-pointer"
                    onClick={() => openLogDetail(log)}
                    data-testid={`audit-row-${log.id}`}
                  >
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-mono text-sm">{formatDate(log.timestamp)}</span>
                        <span className="text-xs text-muted-foreground">{formatTime(log.timestamp)}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <EntityTypeBadge type={log.resource_type} />
                        <span className="font-mono text-xs text-muted-foreground truncate max-w-[150px]">
                          {log.resource_id?.slice(0, 8)}...
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <ActionBadge action={log.action} />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm truncate max-w-[150px]" title={log.user_email || log.user_id}>
                          {log.user_name || log.user_email?.split('@')[0] || log.user_id?.slice(0, 8) + '...'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                      {getSummaryText(log)}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {logs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No audit logs found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of {total}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Detail Drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent className="w-[500px] sm:w-[600px] bg-card border-border overflow-hidden" data-testid="audit-detail-drawer">
          <SheetHeader className="mb-4">
            <SheetTitle className="font-manrope flex items-center gap-3">
              <ActionBadge action={selectedLog?.action} />
              <EntityTypeBadge type={selectedLog?.resource_type} />
            </SheetTitle>
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <Clock className="w-4 h-4" />
              {formatDateTime(selectedLog?.timestamp)}
            </p>
          </SheetHeader>

          {selectedLog && (
            <Tabs defaultValue="diff" className="h-[calc(100vh-150px)]">
              <TabsList className="grid w-full grid-cols-4 mb-4">
                <TabsTrigger value="diff">Diff</TabsTrigger>
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="before">Before</TabsTrigger>
                <TabsTrigger value="after">After</TabsTrigger>
              </TabsList>
              
              <ScrollArea className="h-full pr-4">
                <TabsContent value="diff" className="mt-0">
                  <Card className="bg-background/50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">Changes</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <JsonDiff 
                        before={selectedLog.before || {}} 
                        after={selectedLog.after || selectedLog.details || {}} 
                      />
                    </CardContent>
                  </Card>
                </TabsContent>
                
                <TabsContent value="details" className="mt-0 space-y-4">
                  <Card className="bg-background/50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">Event Details</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Event ID</p>
                          <p className="font-mono text-xs">{selectedLog.id}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Resource ID</p>
                          <p className="font-mono text-xs">{selectedLog.resource_id}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">User</p>
                          <p className="font-medium">{selectedLog.user_name || selectedLog.user_email?.split('@')[0] || 'Unknown'}</p>
                          {selectedLog.user_email && (
                            <p className="text-xs text-muted-foreground">{selectedLog.user_email}</p>
                          )}
                        </div>
                        <div>
                          <p className="text-muted-foreground">Org ID</p>
                          <p className="font-mono text-xs">{selectedLog.org_id}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {selectedLog.entity_current && (
                    <Card className="bg-background/50">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Current Entity State</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <RawJson data={selectedLog.entity_current} />
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>
                
                <TabsContent value="before" className="mt-0">
                  <Card className="bg-background/50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">Before State</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <RawJson data={selectedLog.before} />
                    </CardContent>
                  </Card>
                </TabsContent>
                
                <TabsContent value="after" className="mt-0">
                  <Card className="bg-background/50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">After State / Details</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <RawJson data={selectedLog.after || selectedLog.details} />
                    </CardContent>
                  </Card>
                </TabsContent>
              </ScrollArea>
            </Tabs>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default AuditLogPage;
